'use client';

/**
 * Shared legal policy modal (GitLab #649).
 *
 * Lifted out of `PoweredByPaylinkFooter` so the consent gate can show the
 * cookie policy too. The gate blocks the page, which puts the footer behind
 * the overlay and out of reach — and the moment a customer most needs the
 * policy is *before* they choose, not after. One component so the wording a
 * customer reads at the gate and the wording in the footer cannot drift.
 *
 * Built on Radix's dialog primitive rather than a bare `createPortal`. When
 * the consent gate is open Radix marks the document body `pointer-events:
 * none` and traps focus inside the gate; a portal outside its layer stack
 * renders on top but takes no clicks, so the policy could be opened and not
 * closed. As a nested Radix layer this one becomes the topmost, and the
 * close button, the overlay and Escape all work while the gate underneath
 * stays blocked.
 *
 * An organisation's own markdown wins where it has authored any; otherwise
 * the Paylink default below is shown.
 */
import { c as _c } from "react/compiler-runtime";
import { type ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cookie, FileText, Shield, X } from 'lucide-react';
import { MarkdownText } from '@/components/ui/markdown-text';
import PrivacyModalContent from '@/components/landing/PrivacyModalContent';
import type { LegalContentSettings } from '@/types/organization';
export type LegalPolicyType = 'privacy' | 'terms' | 'cookies';
const POLICY_CONFIG: Record<LegalPolicyType, {
  title: string;
  icon: ReactNode;
}> = {
  privacy: {
    title: 'Privacy Policy',
    icon: <Shield className="h-5 w-5 text-white" />
  },
  terms: {
    title: 'Terms & Conditions',
    icon: <FileText className="h-5 w-5 text-white" />
  },
  cookies: {
    title: 'Cookie Policy',
    icon: <Cookie className="h-5 w-5 text-white" />
  }
};
export interface LegalPolicyModalProps {
  /** Which policy to show; `null` renders nothing. */
  policy: LegalPolicyType | null;
  onClose: () => void;
  /** The organisation's authored content, where it has any. */
  legalContent?: LegalContentSettings;
  /**
   * Stacking order. The default sits above the app; the gate passes a higher
   * value so the policy is readable over the blocking overlay.
   */
  zIndexClass?: string;
}
export function LegalPolicyModal(t0) {
  const $ = _c(30);
  const {
    policy,
    onClose,
    legalContent,
    zIndexClass: t1
  } = t0;
  const zIndexClass = t1 === undefined ? "z-[100]" : t1;
  if (!policy) {
    return null;
  }
  const authored = policy === "privacy" ? legalContent?.privacy_policy : policy === "terms" ? legalContent?.terms_conditions : legalContent?.cookie_policy;
  let t2;
  if ($[0] !== onClose) {
    t2 = next => {
      if (!next) {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `fixed inset-0 ${zIndexClass} bg-black/70 backdrop-blur-sm`;
  let t4;
  if ($[2] !== t3) {
    t4 = <DialogPrimitive.Overlay className={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = `fixed left-[50%] top-[50%] ${zIndexClass} w-[calc(100%-2rem)] max-w-3xl max-h-[85vh] translate-x-[-50%] translate-y-[-50%] overflow-y-auto glass-strong rounded-2xl border border-cyan-500/20 shadow-2xl outline-none animate-in fade-in zoom-in-95 duration-200`;
  const t6 = POLICY_CONFIG[policy];
  let t7;
  if ($[4] !== t6.icon) {
    t7 = <div className="inline-flex p-2.5 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500">{t6.icon}</div>;
    $[4] = t6.icon;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = POLICY_CONFIG[policy];
  let t9;
  if ($[6] !== t8.title) {
    t9 = <DialogPrimitive.Title className="text-xl font-bold text-foreground">{t8.title}</DialogPrimitive.Title>;
    $[6] = t8.title;
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  let t10;
  if ($[8] !== t7 || $[9] !== t9) {
    t10 = <div className="flex items-center gap-3">{t7}{t9}</div>;
    $[8] = t7;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const t11 = `Close ${POLICY_CONFIG[policy].title}`;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <X className="h-5 w-5" />;
    $[11] = t12;
  } else {
    t12 = $[11];
  }
  let t13;
  if ($[12] !== t11) {
    t13 = <DialogPrimitive.Close aria-label={t11} className="text-muted-foreground hover:text-foreground transition-colors p-1">{t12}</DialogPrimitive.Close>;
    $[12] = t11;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== t10 || $[15] !== t13) {
    t14 = <div className="sticky top-0 z-10 flex items-start justify-between p-6 pb-4 border-b border-white/10 glass-strong rounded-t-2xl">{t10}{t13}</div>;
    $[14] = t10;
    $[15] = t13;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== authored || $[18] !== policy) {
    t15 = <div className="p-6 space-y-8">{authored ? <MarkdownText content={authored} className="prose-sm prose-invert max-w-none" /> : policy === "privacy" ? <PrivacyModalContent /> : policy === "terms" ? <TermsContent /> : <CookiePolicyContent />}</div>;
    $[17] = authored;
    $[18] = policy;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t14 || $[21] !== t15 || $[22] !== t5) {
    t16 = <DialogPrimitive.Content aria-describedby={undefined} className={t5}>{t14}{t15}</DialogPrimitive.Content>;
    $[20] = t14;
    $[21] = t15;
    $[22] = t5;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t16 || $[25] !== t4) {
    t17 = <DialogPrimitive.Portal>{t4}{t16}</DialogPrimitive.Portal>;
    $[24] = t16;
    $[25] = t4;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== t17 || $[28] !== t2) {
    t18 = <DialogPrimitive.Root open={true} onOpenChange={t2}>{t17}</DialogPrimitive.Root>;
    $[27] = t17;
    $[28] = t2;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  return t18;
}

/* ------------------------------------------------------------------ */
/*  Default Paylink content                                           */
/* ------------------------------------------------------------------ */

export function Section(t0) {
  const $ = _c(8);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="h-1 w-6 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">{t1}{title}</h3>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children) {
    t3 = <div className="space-y-3 text-muted-foreground leading-relaxed text-sm">{children}</div>;
    $[3] = children;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="space-y-3">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
export function TermsContent() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Section title="Who We Are"><p>We are Nyhavn Limited, trading as Paylink, a company incorporated and registered in England and Wales with company number 15313717. Our registered office is at Kempton House, Kempton Way, Grantham, Lincolnshire, NG31 7LE.</p><p>When we mention "we", "us" or "our", we mean Nyhavn Limited trading as Paylink. When we refer to "you" we mean the user of our services.</p></Section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Section title="About these Terms and Conditions"><p>These Terms and Conditions make a legally binding contract between us and you. By using our services you agree to comply with and be bound by the following Terms and Conditions of Use, which together with our Privacy Policy govern our relationship with you. If you disagree with any part of these Terms and Conditions, please do not use our services.</p><p>We reserve the right to make changes to these Terms and Conditions from time to time. Your continued use of our services will constitute your acceptance of any changes.</p></Section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Section title="Intellectual Property"><p>All content is protected by UK and International Copyright Law and may not be replicated or republished without prior written permission from Paylink.</p></Section>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Section title="Limitation of Liability"><p>Whilst every effort has been made to ensure that the information provided is accurate, we do not accept liability for any errors or omissions contained within our services. We do not warrant that the service will be uninterrupted or error-free.</p></Section>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <>{t0}{t1}{t2}{t3}<Section title="Governing Law"><p>Your use of our services and any dispute arising out of such use is subject to the laws of England and Wales.</p></Section></>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export function CookiePolicyContent() {
  const $ = _c(8);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Section title="What are cookies?"><p>Cookies are small text files placed on your device when you use a website or an app. They are widely used to make a service work, and to tell the people who run it how it is being used.</p></Section>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p>We use cookies for these purposes:</p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Section title="How we use cookies">{t1}<ul className="list-disc pl-5 space-y-1"><li><strong className="text-foreground">Strictly necessary</strong> — these sign you in, keep you signed in, protect forms from abuse and remember the privacy choice you make. The service will not work without them, so they cannot be turned off.</li><li><strong className="text-foreground">Analytics</strong> — these show us how the service is used and where people get stuck, so we can improve it. They are only set if you say yes.</li><li><strong className="text-foreground">Marketing</strong> — these tell us whether an advert brought you here. They are only set if you say yes, and we do not use them to build an advertising profile of you.</li></ul><p>Exactly which cookies fall under each purpose, who sets them and how long they last is listed under "Manage preferences" on the cookie notice, and under "Cookie settings" at the foot of any page.</p></Section>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Section title="Changing your mind"><p>You can change or withdraw your choice at any time from "Cookie settings" at the foot of any page. Withdrawing deletes the optional cookies already on your device — it does not simply stop new ones being set. Your choice is remembered for 180 days, after which we ask again.</p><p>Most browsers also let you block or delete cookies yourself. If you block the strictly necessary ones, parts of the service will stop working.</p></Section>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Section title="Third-party cookies"><p>Some optional cookies are set by trusted third parties on our behalf, such as Google Analytics. Nothing is set by them until you have given permission. Each provider is named in the cookie table under "Manage preferences".</p></Section>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Section title="Changes to this policy"><p>We may update this Cookie Policy from time to time. Where a change affects what you have agreed to, we will ask you to make your choice again.</p></Section>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p>If you have any questions about our use of cookies, please contact us at:</p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <>{t0}{t2}{t3}{t4}{t5}<Section title="Contact us">{t6}<ul className="list-disc pl-5 space-y-1"><li>Email: <a href="mailto:enquiries@paylinksolutions.co.uk" className="text-cyan-400 hover:underline">enquiries@paylinksolutions.co.uk</a></li><li>Telephone: 0800 072 1204</li></ul></Section></>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}